import React, { useCallback, useContext } from 'react';
import classNames from 'classnames';
import CodeRowNumber from './CodeRowNumber';
import CodeField from './CodeField';
import CodeCategoryField from './CodeCategoryField';
import BundleIndicatorField from './BundleIndicatorField';
import CodeDescriptionField from './CodeDescriptionField';
import LegacySalePriceField from './LegacySalePriceField';
import CodeLocationField from './CodeLocationField';
import CodeMappingField from './CodeMappingField';
import RowActionsField from './CodeRowActions/RowActionsField';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { DaylightContext } from '../../../../DaylightCodeMapper';
import CodeUsageDetailField from './CodeUsageDetailField';
import CodeTTMField from './CodeTTMField';
import { actions } from '../../../../../../../../context';

const CodeRow = ({ code, codeIndex, style, mappingIndex, filteredCodeIds = [] }) => {
    const dispatch = useDispatch();

    const displayCodeLabel = (index, vals = []) =>
        index === 0 || (index % 5 === 0 && index < vals.length - 1);

    const formatLegacySalePrice = (codePrice) => {
        return codePrice
            ? `$${(Math.round(codePrice * 100) / 100).toFixed(2)}`
            : 'n/a';
    };

    const formatCodeTTM = (codeTTM) => {
        return codeTTM === 0
            ? '0'
            : codeTTM
            ? `$${(Math.round(codeTTM * 100) / 100).toFixed(2)}`
            : 'n/a';
    };

    // TODO: make sure context values are working correctly
    const { canFlagForReview, canReview, codeUsageStatsMap } =
        useContext(DaylightContext);

    const selectedCodeIds = useSelector(
        (state) => state.daylight.selectedCodeIds,
        shallowEqual
    );

    const practiceIsFinalized = useSelector(
        (state) => state.daylight.practiceIsFinalized
    );

    const lastSelectedCodeId = useSelector(
        (state) => state.daylight.lastSelectedCodeId
    );

    const isSelected = selectedCodeIds.includes(code.id);

    const handleCheckboxChange = useCallback((e) => {
        if (e.shiftKey && lastSelectedCodeId && filteredCodeIds.length > 0) {
            const lastIndex = filteredCodeIds.indexOf(lastSelectedCodeId);
            const currentIndex = filteredCodeIds.indexOf(code.id);
            if (lastIndex !== -1 && currentIndex !== -1) {
                const start = Math.min(lastIndex, currentIndex);
                const end = Math.max(lastIndex, currentIndex);
                const rangeIds = filteredCodeIds.slice(start, end + 1);
                const merged = [...new Set([...selectedCodeIds, ...rangeIds])];
                dispatch(actions.daylight.selectRangeOfCodes(merged));
                return;
            }
        }
        if (isSelected) {
            dispatch(actions.daylight.deselectCode(code.id));
        } else {
            dispatch(actions.daylight.selectCode(code.id));
        }
    }, [dispatch, isSelected, code.id, lastSelectedCodeId, filteredCodeIds, selectedCodeIds]);

    const addMappingOpen = useSelector(
        (state) => state.daylight.isAddMappingModalOpen
    );
    const editMappingOpen = useSelector(
        (state) => state.daylight.isEditMappingModalOpen
    );

    const commentsModalOpen = useSelector(
        (state) => state.daylight.isMappingCommentModalOpen
    );

    // TODO: implement actual diffing
    // TODO: does this need to be "targetCode" or can it feasibly be "updatingCodeId"?
    const selectedCode = useSelector(
        (state) => state.daylight.actionTargetCode,
        shallowEqual
    );
    // TODO: implement actual diffing
    const selectedMapping = useSelector(
        (state) => state.daylight.actionTargetMapping,
        shallowEqual
    );

    const selectedCodeUsageDetail = useSelector(
        (state) => state.daylight.selectedCodeUsageDetail,
        shallowEqual
    );

    const displayUsageDetails = useSelector(
        (state) => state.daylight.userHasAccessToCodeUsageDetails,
        shallowEqual
    );

    const getCodeDetail = (codeId) => {
        const codeDetails = codeUsageStatsMap.get(codeId);
        const defaultValue = '';

        switch (selectedCodeUsageDetail) {
            case 'frequency90Day':
                return codeDetails?.frequency90Day ?? defaultValue;
            case 'frequency6Month':
                return codeDetails?.frequency6Month ?? defaultValue;
            case 'matchPercentageYTD':
                return codeDetails?.matchPercentageYTD ?? defaultValue;
            case 'frequencyYTD':
                return codeDetails?.frequencyYTD ?? defaultValue;
            case 'frequencyPreviousYTD':
                return codeDetails?.frequencyPreviousYTD ?? defaultValue;
            case 'yoyChangePercentage':
                return codeDetails?.yoyChangePercentage ?? defaultValue;
            case 'prevFrequency1Year':
                return codeDetails?.prevFrequency1Year ?? defaultValue;
            case 'prevFrequency2Year':
                return codeDetails?.prevFrequency2Year ?? defaultValue;
            case 'prevFrequency3Year':
                return codeDetails?.prevFrequency3Year ?? defaultValue;
            default:
                return defaultValue;
        }
    };

    const canDelete = code.mappings.length === 1 ? false : true;
    const getRowClasses = (mapping, rowIndex) => {
        let classes = ['border-dark'];

        // Add border-top to first row
        if (rowIndex === 0) {
            classes.push('border-top');
        }

        if (isSelected) {
            classes.push('bg-primary text-light');
        } else if (!mapping) {
            classes.push('bg-danger')
        }
        else if (mapping.id === selectedMapping?.id && editMappingOpen) {
            classes.push('bg-warning');
        } else if (mapping.id === selectedMapping?.id && commentsModalOpen) {
            classes.push('bg-warning');
        } else if (code?.id == selectedCode?.code?.id && addMappingOpen) {
            classes.push('bg-success text-light');
        } else if (code.needsReview && (canReview || canFlagForReview)) {
            classes.push('bg-info text-light');
        } else if (
            codeIndex % 2 === 0 &&
            (selectedCode?.code?.id !== code.id ||
                selectedMapping?.id !== mapping.id)
        ) {
            classes.push('bg-dark-opacity-1');
        } else if (mapping.disabled) {
            classes.push('row-disabled');
        }

        return classes.join(' ');
    };
    let mapping = null;
    if (code && code.mappings && code.mappings.length >= mappingIndex + 1) { 
        mapping = code.mappings[mappingIndex];
    }    
    const isBorderTop = mappingIndex === 0;
    const isDisplayCodeLabel = displayCodeLabel(
        mappingIndex,
        code.mappings
    );
    const codeFieldContent = isDisplayCodeLabel ? code.code : '';
    const categoryContent = isDisplayCodeLabel
        ? code.codeCategory
        : ' ';
    const descriptionContent = isDisplayCodeLabel
        ? code.codeDescription
        : ' ';
    const legacySalePriceContent =
        isDisplayCodeLabel && formatLegacySalePrice(code.codePrice);
    const locationContent = mappingIndex === 0 ? code.locationId : '';
    const usageDetailContent = getCodeDetail(code.sourceCodeId);
    const codeTTMContent =
        isDisplayCodeLabel && formatCodeTTM(code.codeTTM);
    const mappingPrefix =
        mapping?.codeName || mapping?.taxonomyCodeNodeLabel;
    const mappingContent = mapping?.taxonomyCodeNodeLabel;
    // TODO: see if possible to pass down "code" primitives in an easy way
    const rowActionsProps = {
        isBorderTop,
        canFlagForReview,
        code,
        mapping: mapping,
        canDelete,
    };

    return (
        <tr
            key={mapping?.id ?? code.id + mappingIndex}
            style={style}
            className={getRowClasses(mapping, mappingIndex)}>
            {!practiceIsFinalized && (
                <td style={{ width: '36px', minWidth: '36px', verticalAlign: 'middle', textAlign: 'center' }}>
                    {mappingIndex === 0 && (
                        <input
                            type="checkbox"
                            checked={isSelected}
                            onClick={handleCheckboxChange}
                            onChange={() => {}}
                            style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                        />
                    )}
                </td>
            )}
            <CodeRowNumber
                rowIndex={mappingIndex}
                codeRowField={code.row}
            />

            <CodeCategoryField codeCategory={categoryContent} />

            <BundleIndicatorField
                isBundleParent={code.isBundleParent}
                show={mappingIndex === 0}
            />

            <CodeField content={codeFieldContent} />

            <CodeDescriptionField
                codeDescription={descriptionContent}
                // height={isDisplayCodeLabel ? '70px' : '40px'}
            />
            <LegacySalePriceField
                legacySalePrice={legacySalePriceContent}
            />
            <CodeLocationField
                borderTop={isBorderTop}
                location={locationContent}
            />
            {displayUsageDetails && (
                <CodeUsageDetailField
                    content={usageDetailContent}
                />
            )}
            <CodeTTMField
                codeTTM={codeTTMContent}
            />
            <CodeMappingField
                prefix={mappingPrefix}
                content={mappingContent}
            />
            <RowActionsField {...rowActionsProps} />
        </tr>
    );
};

export default CodeRow;
